import { useState } from 'react';
import { useShellTexts } from '../i18n/use-shell-texts';
import { inputAddonClass, inputBaseClass } from './form/input';
import { Icon } from './icon';
import { useDebouncedCallback } from './use-debounced-callback';

/**
 * A search box: reports what was typed after a pause (300 ms, as Angular), and at once when
 * cleared. Ported from portal-ui-lib's `search-input`, drawn as the legacy input group with a
 * search addon box on the left.
 */
export function SearchInput({
  value = '',
  onSearch,
  placeholder,
  label,
  debounceMs = 300,
  disabled = false,
  className = '',
}: {
  /** The search currently applied; the box starts from it. */
  value?: string;
  onSearch: (value: string) => void;
  placeholder?: string;
  /** Accessible name; defaults to the placeholder. */
  label?: string;
  debounceMs?: number;
  disabled?: boolean;
  className?: string;
}) {
  const t = useShellTexts().kit;
  const [text, setText] = useState(value);
  const debounced = useDebouncedCallback(onSearch, debounceMs);
  const shownPlaceholder = placeholder ?? t.search;
  return (
    <div className={`relative flex items-stretch ${className}`}>
      <span className={`${inputAddonClass} rounded-l-bs`} aria-hidden="true">
        <Icon name="search" />
      </span>
      <input
        type="search"
        value={text}
        disabled={disabled}
        placeholder={shownPlaceholder}
        aria-label={label ?? shownPlaceholder}
        onChange={event => {
          setText(event.target.value);
          debounced.call(event.target.value.trim());
        }}
        className={`${inputBaseClass} -ml-px min-w-0 flex-1 rounded-r-bs pr-8 focus:z-10 [&::-webkit-search-cancel-button]:hidden`}
      />
      {text ? (
        <button
          type="button"
          aria-label={t.clearSearch}
          onClick={() => {
            setText('');
            debounced.cancel();
            onSearch('');
          }}
          className="absolute inset-y-0 right-0 z-20 flex items-center pr-3 text-ink-muted hover:text-ink"
        >
          <Icon name="close" />
        </button>
      ) : null}
    </div>
  );
}
